import {
  QuoteEntry,
  QuoteTemplateGenerationStatus,
  RFXQuotesFileProcessStatus,
  RFXQuotesFileUploadStatus,
  RFXQuoteTemplateGenerationProcessStatus
} from '../../../../interfaces';
import {
  DownloadRFXRequestAttachmentOutput,
  RFXAttachment,
  RFXCommodityInformation,
  RFXQuoteTemplateGenerationStatus
} from '../../../../interfaces/RFXBackendModel';
import { CommodityInformation } from '../../../../interfaces/RFXRequestForm';

export function convertCommodityInformationToFrontendModel(
  commodityInformation: RFXCommodityInformation | undefined
): CommodityInformation {
  let rootId = '';
  let commodityIds: string[] = [];
  let category = undefined;

  if (commodityInformation) {
    rootId = commodityInformation.rootId ? commodityInformation.rootId.value : '';
    commodityIds = commodityInformation.commodityIds
      ? commodityInformation.commodityIds.map((identifier) => identifier.value)
      : [];
    category = commodityInformation.category ? commodityInformation.category.value : undefined;
  }

  return { rootId: rootId, commodityIds: commodityIds, category: category };
}

export function convertDownloadRFXRequestAttachmentOutputToQuoteEntry(
  downloadOutput: DownloadRFXRequestAttachmentOutput
): QuoteEntry {
  const attachment: RFXAttachment = downloadOutput.attachment;
  if (attachment.status === undefined || attachment.createdDate === undefined || attachment.format === undefined) {
    throw new Error(`Downlaoded RFX request attachment has unexpected data - ${JSON.stringify(attachment)}`);
  }

  return {
    attachmentId: downloadOutput.attachment.externalId,
    uploadStatus: RFXQuotesFileUploadStatus[attachment.status.toUpperCase() as keyof typeof RFXQuotesFileUploadStatus],
    processStatus: RFXQuotesFileProcessStatus.SUBMITTED,
    version: Number(attachment.version),
    uploadDate: attachment.createdDate?.value || '',
    fileName: attachment.filename,
    fileURL: downloadOutput.url,
    fileSize: attachment.size || '',
    format: attachment.format,
    stagingS3Uri: attachment.stagingS3Uri,
    report: '',
    reportURL: '',
    message: '',
    description: attachment.description
  };
}

/**
 * Converts quote template generation status backend model to frontend model.
 * If backend model is undefined, then returns undefined frontend model.
 * Otherwise, sets the attempt, status, optional message, and optional file details.
 */
export function convertQuoteTemplateGenerationStatusBackendModelToFrontendModel(
  quoteTemplateGenerationStatusBackendModel: RFXQuoteTemplateGenerationStatus
): QuoteTemplateGenerationStatus {
  let quoteTemplateGenerationStatusFrontendModel: QuoteTemplateGenerationStatus;

  const attempt: number = quoteTemplateGenerationStatusBackendModel.attempt;
  const generationStatus: RFXQuoteTemplateGenerationProcessStatus = getGenerationStatusForStringRequestState(
    quoteTemplateGenerationStatusBackendModel.requestState
  );
  const message: string | undefined = quoteTemplateGenerationStatusBackendModel.message;
  const fileName: string | undefined = getFileNameForGenerationStatus(
    quoteTemplateGenerationStatusBackendModel,
    generationStatus
  );
  const fileURL: string | undefined = getFileURLForGenerationStatus(
    quoteTemplateGenerationStatusBackendModel,
    generationStatus
  );

  quoteTemplateGenerationStatusFrontendModel = {
    attempt: attempt,
    generationStatus: generationStatus,
    message: message,
    fileName: fileName,
    fileURL: fileURL
  };

  return quoteTemplateGenerationStatusFrontendModel;
}

/**
 * Retrieves generation status for the provided request state.
 */
function getGenerationStatusForStringRequestState(requestState: string): RFXQuoteTemplateGenerationProcessStatus {
  return RFXQuoteTemplateGenerationProcessStatus[requestState as keyof typeof RFXQuoteTemplateGenerationProcessStatus];
}

/**
 * Retrieves file name for generation status and backend model provided.
 */
function getFileNameForGenerationStatus(
  quoteTemplateGenerationStatusBackendModel: RFXQuoteTemplateGenerationStatus,
  generationStatus: RFXQuoteTemplateGenerationProcessStatus
): string | undefined {
  let fileNameToReturn: string | undefined = undefined;

  if (RFXQuoteTemplateGenerationProcessStatus.FAILED === generationStatus) {
    fileNameToReturn = quoteTemplateGenerationStatusBackendModel.errorReportS3Key;
  } else if (RFXQuoteTemplateGenerationProcessStatus.COMPLETED === generationStatus) {
    fileNameToReturn = quoteTemplateGenerationStatusBackendModel.quoteTemplateS3Key;
  }

  // If the file name exists, then need to breakdown the S3 key first.
  // Split the key based on '/', which delimits the folder structure.
  // Retrieve the last element of the split.
  if (fileNameToReturn !== undefined) {
    let s3KeySplit = fileNameToReturn.split('/');
    fileNameToReturn = s3KeySplit[s3KeySplit.length - 1];
  }

  return fileNameToReturn;
}

/**
 * Retrieves file URL for generation status and backend model provided.
 */
function getFileURLForGenerationStatus(
  quoteTemplateGenerationStatusBackendModel: RFXQuoteTemplateGenerationStatus,
  generationStatus: RFXQuoteTemplateGenerationProcessStatus
): string | undefined {
  let fileURLToReturn: string | undefined = undefined;

  if (RFXQuoteTemplateGenerationProcessStatus.FAILED === generationStatus) {
    fileURLToReturn = quoteTemplateGenerationStatusBackendModel.errorReportURL;
  } else if (RFXQuoteTemplateGenerationProcessStatus.COMPLETED === generationStatus) {
    fileURLToReturn = quoteTemplateGenerationStatusBackendModel.quoteTemplateURL;
  }

  return fileURLToReturn;
}
